import { useCallback, useEffect, useRef, useState } from "react";

import {
  dashcamProtocol,
  dashcamStreamUrl,
  dashcamUnitAliases,
  dashcamUnitId,
  dashcamWhepStreamUrl,
  dashcamWhepUrl,
  isDashcamProtocolPlayable,
  normalizeUnitId,
  type DashcamCamera,
  type DashcamUnit,
} from "../services/dashcam";
import type { CommandStatus } from "../store/admin/deviceCommandsStore";
import type {
  DashcamLiveVideo,
  DashcamLiveVideoPhase,
  DashcamSmsSupport,
} from "./useDashcamLiveVideo";

export const DASHCAM_CONNECTION_STATES = [
  "idle",
  "connecting",
  "connected",
  "error",
] as const;

export type DashcamConnectionState = (typeof DASHCAM_CONNECTION_STATES)[number];

export interface DashcamAlert {
  event: string;
  level: "high" | "medium";
}

export interface DashcamStreamState {
  stream: MediaStream | null;
  connectionState: DashcamConnectionState;
  alert: DashcamAlert | null;
  error: string | null;
  exhausted: boolean;
}

export interface DashcamStreamData extends DashcamStreamState {
  readOnly: boolean;
  commandPhase: DashcamLiveVideoPhase;
  commandStatus: CommandStatus | null;
  smsSupport: DashcamSmsSupport;
  canRetry: boolean;
  retry: () => void;
  force: () => void;
}

interface RelaySignalMessage {
  type?: string;
  unit?: string;
  from?: string;
  sdp?: string | RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  event?: string;
  level?: "high" | "medium";
}

const ALERT_TIMEOUT_MS = 6000;
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 8000;
const RETRY_LIMIT = 3;
const RECOVERY_GRACE_MS = 5000;
const NO_STREAM_RETRY_MS = 2000;
const NO_STREAM_LIMIT = 12;
const ICE_GATHERING_TIMEOUT_MS = 2000;

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

const IDLE_STATE: DashcamStreamState = {
  stream: null,
  connectionState: "idle",
  alert: null,
  error: null,
  exhausted: false,
};

function waitForIceGatheringComplete(
  connection: RTCPeerConnection,
  timeoutMs = ICE_GATHERING_TIMEOUT_MS,
): Promise<void> {
  if (connection.iceGatheringState === "complete") return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      connection.removeEventListener("icegatheringstatechange", onChange);
      window.clearTimeout(timer);
      resolve();
    };

    const onChange = () => {
      connection.iceGatheringState === "complete" && finish();
    };

    connection.addEventListener("icegatheringstatechange", onChange);
    const timer = window.setTimeout(finish, timeoutMs);
  });
}

export function useDashcamStream(
  unit: DashcamUnit | null,
  camera: DashcamCamera | null = null,
  liveVideo: DashcamLiveVideo,
): DashcamStreamData {
  const [data, setData] = useState<DashcamStreamState>(IDLE_STATE);
  const [manualAttempt, setManualAttempt] = useState(0);

  const liveVideoRetry = liveVideo.retry;
  const liveVideoForce = liveVideo.force;

  const retry = useCallback(() => {
    liveVideoRetry();
    setManualAttempt((value) => value + 1);
  }, [liveVideoRetry]);

  const force = useCallback(() => {
    liveVideoForce();
    setManualAttempt((value) => value + 1);
  }, [liveVideoForce]);

  const protocol = unit ? dashcamProtocol(unit.dashCam) : null;
  const imei = unit?.imei ?? null;
  const commandReady = liveVideo.ready;
  const readOnly = liveVideo.readOnly;

  const url =
    protocol === "wss"
      ? unit
        ? dashcamStreamUrl(unit.dashCam)
        : null
      : protocol === "whep" && imei
        ? dashcamWhepStreamUrl(imei, camera)
        : null;

  const unitId = unit ? dashcamUnitId(unit) : "";
  const aliasKey = unit ? dashcamUnitAliases(unit).join("|") : "";
  const playable = unit ? isDashcamProtocolPlayable(unit.dashCam) : true;

  const mountedRef = useRef(true);

  const reportStream = liveVideo.reportStream;
  const streamKey = camera ?? "single";
  const mounted = Boolean(url && unitId && playable);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    reportStream(streamKey, mounted ? !data.exhausted : null);
    return () => reportStream(streamKey, null);
  }, [reportStream, streamKey, mounted, data.exhausted]);

  useEffect(() => {
    if (!url || !unitId) {
      setData(IDLE_STATE);
      return;
    }

    if (!playable) {
      setData({ ...IDLE_STATE, connectionState: "error", error: "protocolUnsupported" });
      return;
    }

    if (readOnly) {
      setData({ ...IDLE_STATE, connectionState: "error", error: "readOnly" });
      return;
    }

    if (!commandReady) {
      setData({ ...IDLE_STATE, connectionState: "connecting" });
      return;
    }

    let socket: WebSocket | null = null;
    let peer: RTCPeerConnection | null = null;
    let resourceUrl: string | null = null;
    let retryTimer: number | null = null;
    let noStreamAttempt = 0;
    let alertTimer: number | null = null;
    let graceTimer: number | null = null;
    let attempt = 0;
    let stopped = false;

    const aliases = new Set([normalizeUnitId(unitId), ...aliasKey.split("|")].filter(Boolean));
    const matchesUnit = (value?: string) => aliases.has(normalizeUnitId(value));

    const update = (patch: Partial<DashcamStreamState>) =>
      mountedRef.current && !stopped && setData((prev) => ({ ...prev, ...patch }));

    const clearGrace = () => {
      graceTimer !== null && window.clearTimeout(graceTimer);
      graceTimer = null;
    };

    const dropPeer = () => {
      clearGrace();
      const current = peer;
      peer = null;
      current?.close();

      if (resourceUrl) {
        const target = resourceUrl;
        resourceUrl = null;
        void fetch(target, { method: "DELETE" }).catch(() => undefined);
      }
    };

    const dropSocket = () => {
      if (!socket) return;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.readyState !== WebSocket.CLOSED && socket.close();
      socket = null;
    };

    const watchConnectionState = (connection: RTCPeerConnection) => {
      connection.onconnectionstatechange = () => {
        if (peer !== connection) return;

        const state = connection.connectionState;

        if (state === "connected") {
          clearGrace();
          return;
        }

        if (state === "disconnected") {
          if (graceTimer !== null) return;
          graceTimer = window.setTimeout(() => {
            graceTimer = null;
            peer === connection &&
              connection.connectionState !== "connected" &&
              update({ connectionState: "error", error: "connectionLost", exhausted: true });
          }, RECOVERY_GRACE_MS);
          return;
        }

        ["failed", "closed"].includes(state) &&
          update({ connectionState: "error", error: "connectionLost", exhausted: true });
      };
    };

    const scheduleRetry = (reason: string) => {
      if (stopped) return;

      if (reason === "whepNoStream" && noStreamAttempt < NO_STREAM_LIMIT) {
        noStreamAttempt += 1;
        update({ stream: null, connectionState: "connecting", error: "whepNoStreamRetrying", exhausted: false });
        retryTimer = window.setTimeout(connect, NO_STREAM_RETRY_MS);
        return;
      }

      if (attempt >= RETRY_LIMIT) {
        update({ stream: null, connectionState: "error", error: reason, exhausted: true });
        return;
      }

      update({ stream: null, connectionState: "error", error: reason });
      const delay = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
      attempt += 1;
      retryTimer = window.setTimeout(connect, delay);
    };

    async function connectWhep() {
      dropPeer();
      update({ connectionState: "connecting", error: null, exhausted: false });

      const connection = new RTCPeerConnection(ICE_SERVERS);
      peer = connection;
      connection.addTransceiver("video", { direction: "recvonly" });
      connection.addTransceiver("audio", { direction: "recvonly" });

      connection.ontrack = (trackEvent) => {
        const [remoteStream] = trackEvent.streams;
        if (!remoteStream) return;
        attempt = 0;
        noStreamAttempt = 0;
        update({ stream: remoteStream, connectionState: "connected", error: null });
      };

      watchConnectionState(connection);

      try {
        const offer = await connection.createOffer();
        await connection.setLocalDescription(offer);
        await waitForIceGatheringComplete(connection);

        if (stopped || peer !== connection) return;

        const sdp = connection.localDescription?.sdp;
        if (!sdp) throw new Error("whepNoLocalSdp");

        const response = await fetch(dashcamWhepUrl(url as string), {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: sdp,
        });

        if (!response.ok) {
          const body = await response.text().catch(() => "");
          const noStream = response.status === 404 || body.includes("no stream is available");
          throw new Error(noStream ? "whepNoStream" : `whep:${response.status}`);
        }

        resourceUrl = response.headers.get("Location");
        const answerSdp = await response.text();

        if (stopped || peer !== connection) return;

        await connection.setRemoteDescription({ type: "answer", sdp: answerSdp });
      } catch (err) {
        scheduleRetry(err instanceof Error ? err.message : "whepNegotiationFailed");
      }
    }

    function connectRelay() {
      dropPeer();
      dropSocket();

      const active = new WebSocket(url as string);
      socket = active;
      update({ connectionState: "connecting", error: null, exhausted: false });

      active.onopen = () => {
        active.send(JSON.stringify({ type: "hello", role: "monitor" }));
        active.send(
          JSON.stringify({ type: "watch", unit: unitId, ...(camera ? { channel: camera } : {}) }),
        );
      };

      active.onmessage = async (event) => {
        const message = ((): RelaySignalMessage | null => {
          try {
            return JSON.parse(event.data as string) as RelaySignalMessage;
          } catch {
            return null;
          }
        })();

        if (!message || !matchesUnit(message.unit)) return;

        if (message.type === "offer" && message.sdp) {
          dropPeer();
          const connection = new RTCPeerConnection(ICE_SERVERS);
          peer = connection;

          connection.ontrack = (trackEvent) => {
            const [remoteStream] = trackEvent.streams;
            if (!remoteStream) return;
            attempt = 0;
            update({ stream: remoteStream, connectionState: "connected", error: null });
          };

          connection.onicecandidate = (iceEvent) => {
            iceEvent.candidate &&
              active.readyState === WebSocket.OPEN &&
              active.send(
                JSON.stringify({ type: "ice", unit: unitId, candidate: iceEvent.candidate }),
              );
          };

          watchConnectionState(connection);

          try {
            const description =
              typeof message.sdp === "string"
                ? { type: "offer" as const, sdp: message.sdp }
                : message.sdp;

            await connection.setRemoteDescription(new RTCSessionDescription(description));
            const answer = await connection.createAnswer();
            await connection.setLocalDescription(answer);
            active.readyState === WebSocket.OPEN &&
              active.send(JSON.stringify({ type: "answer", unit: unitId, sdp: answer }));
          } catch {
            update({ connectionState: "error", error: "handshakeFailed" });
          }
          return;
        }

        if (message.type === "ice" && message.from === "unit" && message.candidate) {
          try {
            await peer?.addIceCandidate(new RTCIceCandidate(message.candidate));
          } catch {
            return;
          }
          return;
        }

        if (message.type === "dms") {
          update({
            alert: { event: message.event ?? "", level: message.level ?? "medium" },
          });
          alertTimer !== null && window.clearTimeout(alertTimer);
          alertTimer = window.setTimeout(() => update({ alert: null }), ALERT_TIMEOUT_MS);
        }
      };

      active.onerror = () => update({ error: "socketError" });

      active.onclose = (event) => {
        if (socket !== active) return;
        active.onmessage = null;
        socket = null;
        dropPeer();
        scheduleRetry(`socketClosed:${event.code}`);
      };
    }

    function connect() {
      protocol === "wss" ? connectRelay() : void connectWhep();
    }

    connect();

    return () => {
      stopped = true;
      retryTimer !== null && window.clearTimeout(retryTimer);
      alertTimer !== null && window.clearTimeout(alertTimer);
      dropPeer();
      clearGrace();
      dropSocket();
      mountedRef.current && setData(IDLE_STATE);
    };
  }, [url, unitId, aliasKey, camera, playable, protocol, readOnly, commandReady, manualAttempt]);

  const commandFailed = liveVideo.phase === "failed";

  return {
    ...data,
    connectionState: commandFailed ? "error" : commandReady ? data.connectionState : "connecting",
    error: commandFailed ? liveVideo.error : data.error,
    readOnly,
    commandPhase: liveVideo.phase,
    commandStatus: liveVideo.status,
    smsSupport: liveVideo.smsSupport,
    canRetry: !readOnly && (commandFailed || data.exhausted),
    retry,
    force,
  };
}

export default useDashcamStream;
